import React, { useState, useEffect, useRef } from 'react'
import { observer } from 'mobx-react'

import { Page, Document } from '@elo-kit/components/pdf-reader/components/react-pdf-components'
import { LoadingSpinner } from '@elo-kit/components/loading-spinner/LoadingSpinner'
import { useShopStores } from 'shop/hooks/use-store'

import { fetchItem } from 'shop/api/documents.api'
import { cleanupHTMLStyleFonts } from 'utils/fonts.utils'

import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import './docs-resolver.scss'

interface Params {
  show_mobile?: string
  type?: string
  username?: string
  disable_tracker?: boolean
  power_seller?: boolean
}

const DocsResolver: React.FC<{ params: Params }> = observer(({ params }) => {
  const { sellerStore, mobileAppDocumentsStore, trackingCodesStore } = useShopStores()
  const {
    show_mobile: showMobilePreview,
    power_seller: isPowerSeller,
    disable_tracker: disableTracker,
    type,
    username,
  } = params
  const [fileUrl, setFileUrl] = useState('')
  const [loading, setLoading] = useState(true)
  const [content, setContent] = useState('')
  const [numPages, setNumPages] = useState(null)
  const [pageWasTracked, setPageWasTracked] = useState(false)
  const [dimensions, setDimensions] = useState({
    width: null,
    height: null,
  })

  const containerRef = useRef(null)

  useEffect(() => {
    if (showMobilePreview) {
      fetchMobileDocument().then(() => {
        setContainerDimensions()
      })
    } else {
      fetchDocument(Boolean(isPowerSeller)).then(() => {
        setContainerDimensions()
      })
    }

    if (!disableTracker) {
      if (!pageWasTracked) {
        trackingCodesStore.setTrackEventData('shop_sub_page', {
          username: sellerStore.item.username,
          consentForm: sellerStore.item.consentForm,
        })
        trackingCodesStore.trackEvent()
      }

      setPageWasTracked(true)
    }

    window.addEventListener('resize', setContainerDimensions)

    return () => {
      window.removeEventListener('resize', setContainerDimensions)
      if (!disableTracker) {
        trackingCodesStore.setTrackEventData()
      }
    }
  }, [])

  const setContainerDimensions = () => {
    setDimensions({
      width: containerRef?.current?.offsetWidth,
      height: containerRef?.current?.offsetHeight,
    })
  }

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages)
  }

  const fetchDocument = (isPowerSeller) =>
    fetchItem(
      type === 'accessibility' ? 'accessibility_statement' : type,
      {
        ...sellerStore.getAllUrlParams(),
        username,
      },
      isPowerSeller
    ).then((resp) => {
      if (resp.success) {
        setFileUrl(resp.data.fileUrl)
        setContent(resp.data.content)
        setLoading(false)
      }
    })

  const fetchMobileDocument = () =>
    mobileAppDocumentsStore
      .fetchItem(type === 'accessibility' ? 'accessibility_statement' : type, { sellerId: sellerStore.item.id })
      .then((resp) => {
        if (resp.success) {
          setFileUrl(resp.data.fileUrl)
          setContent(resp.data.content)
          setLoading(false)
        }
      })

  const renderDocNotVisible = () => (
    <div className='mt-2'>
      <a target='_blank' rel='noopener noreferrer' href={fileUrl}>
        {I18n.t('react.shop.doc_not_visible')}
      </a>
    </div>
  )

  const renderContent = () => {
    if (loading) {
      return (
        <div>
          <LoadingSpinner />
        </div>
      )
    }

    if (fileUrl) {
      return (
        <div className='pdf-reader'>
          <Document
            file={fileUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            noData={renderDocNotVisible()}
            error={renderDocNotVisible()}
          >
            {Array.from(new Array(numPages), (el, index) => (
              <Page key={`page_${index + 1}`} pageNumber={index + 1} width={dimensions.width} />
            ))}
          </Document>
        </div>
      )
    }

    return <div dangerouslySetInnerHTML={{ __html: cleanupHTMLStyleFonts(content) || 'No data' }} />
  }

  return (
    <div className='container my-5 doc-resolver' ref={containerRef}>
      {renderContent()}
    </div>
  )
})

export default DocsResolver
